import * as React from "react";
import { Plus, RefreshCw, ShieldCheck } from "lucide-react";
import {
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { useStudentsList } from "@/hooks/useStudents";
import {
  CONSENT_PURPOSE_LABELS,
  useConsentRecords,
  useUpsertConsent,
  type ConsentPurpose,
} from "@/lib/dpdp/api";

export function DpdpConsentPage() {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "dpdp-consents",
    defaultPageSize: 10,
  });
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [studentId, setStudentId] = React.useState("");
  const [purpose, setPurpose] = React.useState<ConsentPurpose>("data_processing");
  const [granted, setGranted] = React.useState<"granted" | "withdrawn">("granted");
  const [note, setNote] = React.useState("");

  const consentsQuery = useConsentRecords({ limit: pageSize, offset: page * pageSize });
  const learnersQuery = useStudentsList({ status: "active", limit: 200 });
  const upsertMutation = useUpsertConsent();

  const total = consentsQuery.data?.total ?? 0;
  const items = React.useMemo(() => consentsQuery.data?.items ?? [], [consentsQuery.data?.items]);
  const learners = learnersQuery.data?.items ?? [];

  const handleSave = async () => {
    if (!studentId) {
      toast.error("Select a learner.");
      return;
    }
    try {
      await upsertMutation.mutateAsync({
        student_id: studentId,
        purpose,
        granted: granted === "granted",
        note: note.trim() || null,
      });
      toast.success("Consent record saved.");
      setEditorOpen(false);
      setNote("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save consent.");
    }
  };

  return (
    <div className="mx-auto max-w-6xl py-8">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>DPDPA consent</PageHeaderHeading>
            <PageHeaderDescription>
              Capture and honour each learner&apos;s data-consent under India&apos;s DPDP Act.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void consentsQuery.refetch()}
              disabled={consentsQuery.isFetching}
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", consentsQuery.isFetching && "animate-spin")}
              />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setEditorOpen(true)}>
              <Plus aria-hidden="true" className="size-4" />
              Record consent
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      <div className="mt-6">
        <DataTable>
          <DataTableToolbar>
            <ShieldCheck aria-hidden="true" className="text-muted-foreground size-4" />
            <span className="text-muted-foreground text-sm">Consent ledger</span>
            <DataTableToolbarSpacer />
          </DataTableToolbar>
          {consentsQuery.isLoading ? (
            <DataTableSkeleton columns={5} rows={6} />
          ) : consentsQuery.isError ? (
            <div className="border-border/60 bg-muted/30 text-muted-foreground rounded-md border p-6 text-sm">
              Could not load consent records.{" "}
              <button
                type="button"
                className="underline"
                onClick={() => void consentsQuery.refetch()}
              >
                Retry
              </button>
            </div>
          ) : items.length === 0 ? (
            <DataTableEmpty
              title="No consent records yet"
              description="Record consent when a learner joins or when you change how their data is used."
              action={
                <Button size="sm" onClick={() => setEditorOpen(true)}>
                  Record consent
                </Button>
              }
            />
          ) : (
            <>
              <DataTableScroll>
                <Table>
                  <caption className="sr-only">Learner consent records</caption>
                  <TableHeader className="bg-background sticky top-0 z-10">
                    <TableRow>
                      <TableHead>Learner</TableHead>
                      <TableHead>Purpose</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Note</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="divide-border/60 divide-y">
                    {items.map((row) => (
                      <TableRow key={row.id} className="hover:bg-muted/40">
                        <TableCell>{row.student_name ?? row.student_id}</TableCell>
                        <TableCell>{CONSENT_PURPOSE_LABELS[row.purpose]}</TableCell>
                        <TableCell>
                          <StatusPill status={row.granted ? "active" : "inactive"} />
                        </TableCell>
                        <TableCell className="text-muted-foreground max-w-xs truncate">
                          {row.note ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DataTableScroll>
              <DataTableFooter>
                <DataTablePagination
                  page={page + 1}
                  pageSize={pageSize}
                  totalRows={total}
                  onPageChange={(next) => setPage(Math.max(0, next - 1))}
                  onPageSizeChange={(next) => {
                    setPageSize(next);
                    setPage(0);
                  }}
                />
              </DataTableFooter>
            </>
          )}
        </DataTable>
      </div>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record consent</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>Learner</Label>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select learner" />
                </SelectTrigger>
                <SelectContent>
                  {learners.map((learner) => (
                    <SelectItem key={learner.id} value={learner.id}>
                      {learner.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Purpose</Label>
              <Select value={purpose} onValueChange={(v) => setPurpose(v as ConsentPurpose)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CONSENT_PURPOSE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Status</Label>
              <Select
                value={granted}
                onValueChange={(v) => setGranted(v as "granted" | "withdrawn")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="granted">Granted</SelectItem>
                  <SelectItem value="withdrawn">Withdrawn</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="consent-note">Note</Label>
              <Textarea
                id="consent-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional context — desk capture, parent request, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={upsertMutation.isPending}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
